import React, { useEffect } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SchoolIcon from "@mui/icons-material/School";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Skeleton } from "@mui/material";
import { readableColor } from "polished";
import { useParams } from "react-router-dom";
import { Navbar } from "../../components/Navbar";
import { useAuth } from "../../contexts/AuthContext";
import { useConfig } from "../../contexts/config";
import { useMoodle } from "../../contexts/MoodleContext";
import { useTheme } from "../../contexts/ThemeContext";
import { resolveCourseGradeAndProgress } from "../../services/moodle/courseFinalGrade";
import { getUserProgressSummary } from "../../services/moodle/courses";
import { filterAllowedCourses } from "../../utils/courses";
import { mapWithConcurrency } from "../../utils/concurrency";
import { KpiCard } from "./KpiCard";
import { ProgressRowItem } from "./ProgressRowItem";
import { ProgressRowItemDetail } from "./ProgressRowItemDetail";
import { ProgressTable } from "./ProgressTable";

const COURSE_RESOLUTION_CONCURRENCY = 6;

const parseNumericGrade = (value) => {
  if (!value || value === "-") return null;
  const parsed = parseFloat(String(value).replace(",", ".").replace(/[^0-9.]/g, ""));
  return Number.isNaN(parsed) ? null : parsed;
};

const getCourseName = (course) => course?.displayname || course?.fullname || "";

const Progress = () => {
  const [loadingGrades, setLoadingGrades] = React.useState(true);
  const [sortConfig, setSortConfig] = React.useState({ key: null, direction: "asc" });
  const [finalGrades, setFinalGrades] = React.useState({});
  const [courseProgress, setCourseProgress] = React.useState({});
  const [summaryData, setSummaryData] = React.useState(null);
  const [pluginState, setPluginState] = React.useState("loading");
  const [selectedCourseId, setSelectedCourseId] = React.useState(null);
  const [selectedCourseName, setSelectedCourseName] = React.useState("");
  const [detailData, setDetailData] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const openedRouteCourseIdRef = React.useRef(null);

  const { id: routeCourseId } = useParams();
  const moodle = useMoodle();
  const theme = useTheme();
  const { lang, user } = useAuth();
  const { config } = useConfig();

  useEffect(() => {
    if (!moodle.courses) {
      moodle.getCourses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user?.token) return;
    let cancelled = false;

    setPluginState("loading");
    setLoadingGrades(true);

    getUserProgressSummary({
      token: user.token,
      userid: user.id || 0,
      baseUrl: config?.baseURL,
    })
      .then((data) => {
        if (cancelled) return;
        if (data && Array.isArray(data.courses)) {
          setSummaryData(data);
          setPluginState("success");
          setLoadingGrades(false);
          return;
        }
        setPluginState("failed");
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn(
            "Tipddy Performance plugin unavailable or malformed, using fallback resolution:",
            err.message,
          );
          setPluginState("failed");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user?.token, user?.id, config?.baseURL]);

  const filteredMoodleCourses = React.useMemo(
    () => (moodle.courses ? filterAllowedCourses(moodle.courses) : []),
    [moodle.courses],
  );

  const courseIdsKey = React.useMemo(
    () => filteredMoodleCourses.map((c) => c.id).join(","),
    [filteredMoodleCourses],
  );

  useEffect(() => {
    if (pluginState !== "failed") return;
    const courses = filteredMoodleCourses;
    if (!courses.length || !user?.token) {
      if (moodle.courses) setLoadingGrades(false);
      return;
    }

    let cancelled = false;
    setFinalGrades({});
    setCourseProgress({});
    setLoadingGrades(true);

    const resolveCourse = async (course) =>
      resolveCourseGradeAndProgress({
        token: user.token,
        id: user.id,
        courseId: course.id,
        baseUrl: config?.baseURL,
      });

    mapWithConcurrency(courses, COURSE_RESOLUTION_CONCURRENCY, resolveCourse, (course, _i, gradeAndProgress) => {
      if (cancelled) return;
      setFinalGrades((prev) => ({ ...prev, [course.id]: gradeAndProgress?.finalGrade ?? null }));
      setCourseProgress((prev) => ({ ...prev, [course.id]: gradeAndProgress?.progress ?? null }));
    }).finally(() => {
      if (!cancelled) setLoadingGrades(false);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pluginState, courseIdsKey, user?.token, user?.id, config?.baseURL]);

  const tableTitles = React.useMemo(() => {
    if (!lang?.History) return null;
    return [
      { label: lang.History.first_column, key: "name", sortable: true },
      { label: lang.History.progress_column || "Progreso", key: "progress", sortable: true, width: "110px" },
      { label: lang.History.second_column, key: "grade", sortable: true, width: "130px" },
      { label: lang.History.third_column, key: "startdate", sortable: true, width: "120px" },
      { label: lang.History.fourth_column, key: "enddate", sortable: true, width: "140px" },
      ...(config?.opciones?.includes("encuesta")
        ? [{ label: lang.History.sixth_column, key: "survey", sortable: false, width: "100px" }]
        : []),
    ];
  }, [lang, config]);

  const detailTitles = React.useMemo(() => {
    if (!lang?.History) return null;
    return [
      { label: lang.History.inner_first_column },
      { label: lang.History.inner_second_column, width: "120px" },
      ...(config?.opciones?.includes("feedback")
        ? [
            { label: lang.History.inner_fourth_column, width: "180px" },
            { label: lang.History.inner_fifth_column, width: "180px" },
          ]
        : []),
    ];
  }, [lang, config]);

  const visibleCourses = React.useMemo(() => {
    const raw = summaryData?.courses ?? filteredMoodleCourses;
    return filterAllowedCourses(raw);
  }, [summaryData, filteredMoodleCourses]);

  const getProgressValue = React.useCallback(
    (course) => {
      if (typeof course?.progress === "number") return course.progress;
      const fallback = courseProgress[course.id];
      if (fallback !== null && fallback !== undefined) return fallback;
      if (course?.progress !== null && course?.progress !== undefined) return Number(course.progress);
      return null;
    },
    [courseProgress],
  );

  const getFinalGrade = React.useCallback(
    (course) => course?.final_grade ?? finalGrades[course.id]?.formatted ?? "-",
    [finalGrades],
  );

  const kpis = React.useMemo(() => {
    const total = visibleCourses.length;
    const completed = visibleCourses.filter((course) => {
      if (course.is_completed !== undefined) return Boolean(course.is_completed);
      const progress = getProgressValue(course);
      return progress !== null && Number(progress) >= 100;
    }).length;
    const inProgress = visibleCourses.filter((course) => {
      if (course.is_completed !== undefined) {
        const progress = Number(course.progress || 0);
        return !course.is_completed && (progress > 0 || getFinalGrade(course) !== "-");
      }
      const progress = getProgressValue(course);
      return progress !== null && Number(progress) > 0 && Number(progress) < 100;
    }).length;

    return { total, completed, inProgress };
  }, [visibleCourses, getProgressValue, getFinalGrade]);

  const sortedCourses = React.useMemo(() => {
    if (!sortConfig.key) return visibleCourses;
    const { key, direction } = sortConfig;
    const modifier = direction === "asc" ? 1 : -1;

    return [...visibleCourses].sort((a, b) => {
      if (key === "name") {
        return getCourseName(a).localeCompare(getCourseName(b), "es", { sensitivity: "base", numeric: true }) * modifier;
      }
      if (key === "progress") {
        const progressA = getProgressValue(a);
        const progressB = getProgressValue(b);
        const valueA = progressA === null || Number.isNaN(Number(progressA)) ? -1 : Number(progressA);
        const valueB = progressB === null || Number.isNaN(Number(progressB)) ? -1 : Number(progressB);
        if (valueA !== valueB) return (valueA - valueB) * modifier;
      }
      if (key === "grade") {
        const gradeA = parseNumericGrade(getFinalGrade(a));
        const gradeB = parseNumericGrade(getFinalGrade(b));
        const hasGradeA = gradeA !== null;
        const hasGradeB = gradeB !== null;
        if (hasGradeA !== hasGradeB) return hasGradeA ? -1 : 1;
        if (hasGradeA && gradeA !== gradeB) return (gradeA - gradeB) * modifier;
      }
      if (key === "startdate") {
        const startA = a.startdate ? Number(a.startdate) : 0;
        const startB = b.startdate ? Number(b.startdate) : 0;
        if (startA !== startB) return (startA - startB) * modifier;
      }
      if (key === "enddate") {
        const hasEndA = Boolean(a.enddate && Number(a.enddate) !== 0);
        const hasEndB = Boolean(b.enddate && Number(b.enddate) !== 0);
        if (hasEndA !== hasEndB) return hasEndA ? -1 : 1;
        if (hasEndA && Number(a.enddate) !== Number(b.enddate)) {
          return (Number(a.enddate) - Number(b.enddate)) * modifier;
        }
      }
      return getCourseName(a).localeCompare(getCourseName(b), "es", { sensitivity: "base", numeric: true });
    });
  }, [visibleCourses, sortConfig, getProgressValue, getFinalGrade]);

  const handleSort = React.useCallback((key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: key === "name" ? "asc" : "desc" };
    });
  }, []);

  const showLoadingRows = (pluginState === "loading" && !summaryData) || loadingGrades;

  const fetchDetail = React.useCallback(
    async (courseId) => {
      setDetailLoading(true);
      setDetailData(null);
      try {
        const [gradesData] = await moodle.getProgressDetail(courseId);
        const rawItems = gradesData?.usergrades?.[0]?.gradeitems ?? [];
        setDetailData(
          rawItems
            .filter((d) => d.itemtype !== "course" && d.itemtype !== "category")
            .filter((d) => d.itemmodule !== "folder")
            .filter((d) => d.itemname !== null && d.itemname !== ""),
        );
      } catch (e) {
        console.log(e.message);
        setDetailData([]);
      } finally {
        setDetailLoading(false);
      }
    },
    [moodle],
  );

  const handleRowClick = React.useCallback(
    (course) => {
      setSelectedCourseId(course.id);
      setSelectedCourseName(getCourseName(course));
      fetchDetail(course.id);
    },
    [fetchDetail],
  );

  React.useEffect(() => {
    if (!routeCourseId) {
      openedRouteCourseIdRef.current = null;
      return;
    }

    const normalizedRouteCourseId = String(routeCourseId);
    if (openedRouteCourseIdRef.current === normalizedRouteCourseId) return;
    if (showLoadingRows || pluginState === "loading" || !moodle.courses) return;

    const candidateCourses = [...(summaryData?.courses ?? []), ...filteredMoodleCourses];
    const matchingCourse = candidateCourses.find((course) => String(course?.id) === normalizedRouteCourseId);

    openedRouteCourseIdRef.current = normalizedRouteCourseId;
    if (matchingCourse) handleRowClick(matchingCourse);
  }, [routeCourseId, showLoadingRows, pluginState, moodle.courses, summaryData, filteredMoodleCourses, handleRowClick]);

  const handleBack = () => {
    setSelectedCourseId(null);
    setSelectedCourseName("");
    setDetailData(null);
  };

  const pageBackground =
    theme.colors.backgroundColorBody ||
    (theme.mode === "light" ? "rgba(0, 0, 0, 0.05)" : "rgb(34, 34, 34)");
  const titleColor = readableColor(pageBackground);

  return (
    <>
      {lang && (
        <>
          <Navbar />
          <div
            className="flex flex-col items-center w-full px-4 py-48 min-h-dvh"
            style={{ backgroundColor: pageBackground, gap: "36px" }}
          >
            <div className="container w-full space-y-3">
              <style>{`
                .progress-panels-wrapper { position: relative; overflow: hidden; }
                .progress-panel { width: 100%; transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease; will-change: transform; }
                .progress-panel-list { transform: translateX(0); opacity: 1; }
                .progress-panel-list.slide-out { transform: translateX(-100%); opacity: 0; position: absolute; top: 0; left: 0; right: 0; pointer-events: none; }
                .progress-panel-detail { transform: translateX(100%); opacity: 0; position: absolute; top: 0; left: 0; right: 0; pointer-events: none; }
                .progress-panel-detail.slide-in { transform: translateX(0); opacity: 1; position: relative; pointer-events: auto; }
              `}</style>

              <div className="flex items-center gap-3">
                {selectedCourseId && (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-bold transition-colors duration-150"
                    style={{
                      backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                      color: theme.colors.textColorDefault,
                    }}
                  >
                    <ArrowBackIcon sx={{ fontSize: 18 }} />
                    Volver
                  </button>
                )}
                <p className="font-medium text-4xl" style={{ color: titleColor }}>
                  {selectedCourseId ? selectedCourseName : lang.History.first_title}
                </p>
              </div>

              {!selectedCourseId && (
                <div className="flex flex-wrap gap-4 w-full pb-2">
                  <KpiCard
                    label={lang.History.kpi_total_courses || "Total courses"}
                    value={showLoadingRows ? <Skeleton variant="text" width={48} /> : kpis.total}
                    icon={<SchoolIcon />}
                    color="#F4E8FF"
                  />
                  <KpiCard
                    label={lang.History.kpi_completed_courses || "Completed"}
                    value={showLoadingRows ? <Skeleton variant="text" width={48} /> : kpis.completed}
                    icon={<CheckCircleIcon />}
                    color="#DCFCE7"
                  />
                  <KpiCard
                    label={lang.History.kpi_courses_in_progress || "In progress"}
                    value={showLoadingRows ? <Skeleton variant="text" width={48} /> : kpis.inProgress}
                    icon={<TrendingUpIcon />}
                    color="#D5F4F8"
                  />
                </div>
              )}

              <div className="progress-panels-wrapper">
                <div className={`progress-panel progress-panel-list${selectedCourseId ? " slide-out" : ""}`}>
                  <ProgressTable headers={tableTitles} sortConfig={sortConfig} onSort={handleSort}>
                    {showLoadingRows
                      ? [...Array(6)].map((_, rowIndex) => (
                          <tr key={`progress-skeleton-${rowIndex}`}>
                            {[...Array(config?.opciones?.includes("encuesta") ? 6 : 5)].map((__, colIndex) => (
                              <td key={colIndex} className="p-[24px] border-none">
                                <Skeleton
                                  variant="rectangular"
                                  height={20}
                                  sx={{ borderRadius: "4px", width: colIndex === 0 ? "80%" : "60%" }}
                                />
                              </td>
                            ))}
                          </tr>
                        ))
                      : sortedCourses.map((course, idx) => (
                          <ProgressRowItem
                            key={course.id ?? idx}
                            course={course}
                            grade={getFinalGrade(course)}
                            progress={getProgressValue(course)}
                            lang={lang}
                            includeEncuesta={config?.opciones?.includes("encuesta")}
                            rowIndex={idx}
                            onRowClick={handleRowClick}
                          />
                        ))}
                  </ProgressTable>
                </div>

                <div className={`progress-panel progress-panel-detail${selectedCourseId ? " slide-in" : ""}`}>
                  {detailLoading ? (
                    <div className="flex flex-col gap-2 p-4">
                      {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: "6px" }} />
                      ))}
                    </div>
                  ) : detailData && detailTitles ? (
                    detailData.length > 0 ? (
                      <ProgressTable headers={detailTitles}>
                        {detailData.map((detail, idx) => (
                          <ProgressRowItemDetail
                            key={idx}
                            detail={detail}
                            index={idx}
                            includeFeedback={config?.opciones?.includes("feedback")}
                          />
                        ))}
                      </ProgressTable>
                    ) : (
                      <div
                        className="flex flex-col items-center justify-center p-12 rounded-xl text-center shadow-sm"
                        style={{
                          backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.03)" : "#FFFFFF",
                          border: `1px solid ${theme.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
                        }}
                      >
                        <p
                          className="font-lato font-medium text-[15px]"
                          style={{ color: theme.mode === "dark" ? "#B0B0B0" : "#4B5563" }}
                        >
                          {lang.History.no_evaluations ||
                            "This course uses an external platform for evaluations."}
                        </p>
                      </div>
                    )
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Progress;
